/**
 * 文档管理组件
 * 提供文档上传、列表展示和删除功能
 * 优化内容：拖拽上传、文件预览语法高亮、进度条、批量上传支持
 */
import { useState, useEffect, useRef } from 'react';
import { Modal, Tooltip, Progress } from 'antd';
import { documentAPI } from '../services/api';

// SVG 图标组件
const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const FileTextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const LoaderIcon = () => (
  <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);

const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);

const FolderOpenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 14 1.45-1a2 2 0 0 1 1.13-.35h.44a2 2 0 0 1 1.59.79l.93 1.16a2 2 0 0 0 1.59.79h.44a2 2 0 0 0 1.59-.79L16.5 14"/>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const DocumentManager = ({ userRole = 'user' }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showPreview, setShowPreview] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  
  const dropZoneRef = useRef(null);
  const isAdmin = userRole === 'admin';

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await documentAPI.list();
      // 按创建时间排序，最新的在上面
      const sortedDocs = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setDocuments(sortedDocs);
    } catch (error) {
      console.error('获取文档列表失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // 拖拽事件处理
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
      
      return () => {
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
      };
    }
  }, []);

  const handleFiles = (files) => {
    const allowedTypes = ['text/plain', 'text/markdown', 'application/json'];
    const allowedExtensions = ['.txt', '.md', '.json'];
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      const filename = file.name.toLowerCase();
      if (allowedTypes.includes(file.type) || allowedExtensions.some(ext => filename.endsWith(ext))) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      Modal.warning({ 
        title: '格式不支持', 
        content: `以下文件格式不支持：${invalidFiles.join(', ')}\n\n支持的格式：.txt, .md, .json` 
      });
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    // 重置input以便可以重复选择相同文件
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    const results = [];
    const totalFiles = selectedFiles.length;
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          await documentAPI.upload(file);
          results.push({ name: file.name, success: true });
        } catch (error) {
          console.error('上传失败:', error);
          results.push({ name: file.name, success: false, error: error.message });
        }
        // 更新进度
        setUploadProgress(((i + 1) / totalFiles) * 100);
      }
      
      setSelectedFiles([]);
      fetchDocuments();
      
      // 显示结果
      const successCount = results.filter(r => r.success).length;
      if (successCount === totalFiles) {
        Modal.success({ title: '全部成功', content: `成功上传 ${totalFiles} 个文件！` });
      } else {
        Modal.info({ 
          title: '上传完成', 
          content: `成功上传 ${successCount} 个文件，失败 ${totalFiles - successCount} 个文件` 
        });
      }
    } catch (error) {
      console.error('批量上传失败:', error);
      Modal.error({ title: '上传失败', content: '请重试' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, filename) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除 "${filename}" 吗？此操作无法撤销。`,
      okText: '确定删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await documentAPI.delete(id);
          fetchDocuments();
        } catch (error) {
          console.error('删除失败:', error);
          Modal.error({ title: '删除失败', content: '请重试' });
        }
      }
    });
  };

  const handlePreview = async (id, filename) => {
    try {
      const response = await documentAPI.get(id);
      setPreviewContent(response.data.content);
      setPreviewFilename(filename);
      setShowPreview(id);
    } catch (error) {
      console.error('获取文档失败:', error);
    }
  };

  const getFileIcon = (filename) => {
    if (filename.toLowerCase().endsWith('.md')) {
      return <span className="text-purple-400"><FileTextIcon /></span>;
    } else if (filename.toLowerCase().endsWith('.json')) {
      return <span className="text-yellow-400"><FileTextIcon /></span>;
    }
    return <span className="text-blue-400"><FileIcon /></span>;
  };

  // 简单的语法高亮
  const highlightContent = (content, filename) => {
    if (filename.toLowerCase().endsWith('.json')) {
      return highlightJson(content);
    }
    if (filename.toLowerCase().endsWith('.md')) {
      return highlightMarkdown(content);
    }
    return escapeHtml(content);
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const highlightJson = (content) => {
    try {
      const parsed = JSON.parse(content);
      const formatted = JSON.stringify(parsed, null, 2);
      return formatted
        .replace(/"([^"]+)":/g, '<span class="text-blue-400">"$1"</span>:')
        .replace(/: "([^"]+)"/g, ': <span class="text-green-400">"$1"</span>')
        .replace(/: (\d+)/g, ': <span class="text-orange-400">$1</span>')
        .replace(/: (true|false|null)/g, ': <span class="text-purple-400">$1</span>');
    } catch {
      return escapeHtml(content);
    }
  };

  const highlightMarkdown = (content) => {
    return content
      .replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold text-blue-400 mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-blue-500 mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-blue-600 mt-8 mb-4">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic text-gray-300">$1</em>')
      .replace(/`([^`]+)`/g, '<code class="bg-white/10 px-2 py-0.5 rounded text-green-400">$1</code>')
      .replace(/^```(\w*)\n([\s\S]*?)```$/gm, '<pre class="bg-black/50 p-4 rounded-lg my-4 overflow-x-auto"><code class="text-gray-300">$2</code></pre>')
      .replace(/^- (.*$)/gim, '<li class="list-disc ml-4 text-gray-300">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="list-decimal ml-4 text-gray-300">$1</li>')
      .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-blue-500 pl-4 italic text-gray-400 my-2">$1</blockquote>')
      .replace(/^\n/g, '<br>');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* 只有管理员才能看到上传区域 */}
      {isAdmin && (
        <div className="glass-card card-hover p-6 mb-6 bg-[#0f0f23]/99 border border-white/5">
          <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
            <span className="text-blue-400 mr-2"><UploadIcon /></span>
            上传文档
          </h2>
          
          {/* 拖拽上传区域 */}
          <div
            ref={dropZoneRef}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
              isDragging 
                ? 'border-blue-400 bg-blue-500/10 scale-[1.02]' 
                : 'border-white/20 hover:border-blue-400/50'
            }`}
          >
            <input
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              accept=".txt,.md,.json"
              multiple
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className={`w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border transition-all ${
                isDragging ? 'border-blue-400 scale-110' : 'border-blue-500/30'
              }`}>
                {isDragging ? <CheckIcon /> : <PlusIcon />}
              </div>
              <p className="text-gray-300 mb-2">
                {isDragging ? '松开以上传文件' : '点击或拖拽文件到此处'}
              </p>
              <p className="text-sm text-gray-500">支持 .txt, .md, .json 格式</p>
            </label>
          </div>
          
          {/* 已选择的文件列表 */}
          {selectedFiles.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-400 mb-3">已选择 {selectedFiles.length} 个文件</h3>
              <div className="space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                        {getFileIcon(file.name)}
                      </div>
                      <div>
                        <p className="text-sm text-gray-200 truncate max-w-xs">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <XIcon />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* 上传按钮和进度 */}
              <div className="mt-4">
                {uploading && (
                  <div className="mb-3">
                    <div className="flex justify-between text-sm text-gray-400 mb-1">
                      <span>上传进度</span>
                      <span>{Math.round(uploadProgress)}%</span>
                    </div>
                    <Progress 
                      percent={uploadProgress} 
                      strokeColor="#3B82F6"
                      showInfo={false}
                      className="bg-white/10"
                    />
                  </div>
                )}
                <button
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles.length === 0}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {uploading ? (
                    <><LoaderIcon /><span>上传中...</span></>
                  ) : (
                    <><UploadIcon /><span>上传文件</span></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="glass-card p-6 bg-[#0f0f23]/99 border border-white/5">
        <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center">
          <span className="text-purple-400 mr-2"><FolderOpenIcon /></span>
          文档列表
          <span className="ml-2 text-sm font-normal text-gray-500">({documents.length})</span>
        </h2>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoaderIcon />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileTextIcon />
            </div>
            <p className="text-gray-500">暂无文档</p>
            <p className="text-sm text-gray-600 mt-1">{isAdmin ? '请上传文档开始使用' : '请联系管理员上传文档'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="glass-card p-4 hover:bg-white/10 transition-all duration-200 group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center border border-white/10">
                      {getFileIcon(doc.filename)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-200 truncate">{doc.filename}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(doc.created_at).toLocaleString('zh-CN')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip title="预览文档">
                      <button
                        onClick={() => handlePreview(doc.id, doc.filename)}
                        className="p-2.5 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors hover:scale-105"
                      >
                        <EyeIcon />
                      </button>
                    </Tooltip>
                    {/* 只有管理员才能看到删除按钮 */}
                    {isAdmin && (
                      <Tooltip title="删除文档">
                        <button
                          onClick={() => handleDelete(doc.id, doc.filename)}
                          className="p-2.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors hover:scale-105"
                        >
                          <TrashIcon />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 文档预览弹窗 */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-card w-full max-w-3xl max-h-[85vh] overflow-hidden animate-scale-in">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                  {getFileIcon(previewFilename)}
                </div>
                <h3 className="font-semibold text-white truncate max-w-md">{previewFilename}</h3>
              </div>
              <button
                onClick={() => setShowPreview(null)}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <XIcon />
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(85vh-80px)]">
              <pre 
                className="whitespace-pre-wrap font-mono text-sm"
                dangerouslySetInnerHTML={{ __html: highlightContent(previewContent, previewFilename) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;